import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2, ArrowLeft, Save } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../../../components/ui/card";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Label } from "../../../../components/ui/label";
import { Switch } from "../../../../components/ui/switch";
import { toast } from "../../../../components/ui/use-toast";
import { PageHeader } from "@/components/ui/page-header";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../components/ui/select";
import { Textarea } from "../../../../components/ui/textarea";
import { useShow, useUpdate, useSelect } from "@refinedev/core";

const CATEGORIES = [
  { value: "hematology", label: "Hematology" },
  { value: "chemistry", label: "Chemistry" },
  { value: "immunology", label: "Immunology" },
  { value: "microbiology", label: "Microbiology" },
  { value: "molecular", label: "Molecular" },
  { value: "urinalysis", label: "Urinalysis" },
  { value: "coagulation", label: "Coagulation" },
  { value: "blood_gas", label: "Blood Gas" },
  { value: "other", label: "Other" },
];
const CONNECTION_TYPES = ["serial", "tcp", "usb", "ethernet", "bluetooth", "wifi"];
const PROTOCOLS = [
  { value: "hl7", label: "HL7 MLLP" }, { value: "astm", label: "ASTM E1394" },
  { value: "lis2a", label: "LIS2-A" }, { value: "clsi", label: "CLSI LIS" },
  { value: "serial", label: "Serial ASCII" }, { value: "tcp", label: "TCP/IP Raw" },
  { value: "custom", label: "Custom" },
];
const BAUD_RATES = ["9600", "19200", "38400", "57600", "115200"];
const PARITIES = [{ value: "N", label: "None" }, { value: "E", label: "Even" }, { value: "O", label: "Odd" }];
const DATA_BITS = ["7", "8"];
const STOP_BITS = ["1", "2"];

export const AnalyzerDeviceEdit: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { query } = useShow({ resource: "devices/analyzers", id });
  const { mutate, isLoading: saving } = useUpdate();
  const { options: connectorOptions } = useSelect({ resource: "devices/connectors", optionLabel: "name", optionValue: "id" });
  const record = query?.data?.data;

  const [form, setForm] = useState<Record<string, any>>({
    name: "", category: "", manufacturer: "", model: "", serial_number: "",
    connector_device: "", connection_type: "tcp", protocol: "hl7", status: "active",
    device_ip: "", device_port: "", lis_ip: "", lis_port: "",
    serial_port: "COM1", baud_rate: "9600", parity: "N", data_bits: "8", stop_bits: "1",
    description: "", supported_tests: "",
    hl7_version: "2.3.1", ack_mode: "original", mllp_enabled: true, message_types: "ORU^R01",
    qc_enabled: true, qc_frequency_hours: "24", calibration_due_date: "", maintenance_due_date: "",
  });
  const set = (f: string, v: any) => setForm((prev) => ({ ...prev, [f]: v }));

  useEffect(() => {
    if (!record) return;
    const cc = record.connection_config || {};
    const pc = record.protocol_config || {};
    setForm({
      name: record.name || "", category: record.category || "", manufacturer: record.manufacturer || "",
      model: record.model || "", serial_number: record.serial_number || "",
      connector_device: record.connector_device ? String(record.connector_device) : "",
      connection_type: record.connection_type || "tcp", protocol: record.protocol || "hl7",
      status: record.status || "active",
      device_ip: record.device_ip || "", device_port: record.device_port?.toString() || "",
      lis_ip: record.lis_ip || "", lis_port: record.lis_port?.toString() || "",
      serial_port: cc.serial_port || "COM1", baud_rate: (cc.baud_rate || 9600).toString(),
      parity: cc.parity || "N", data_bits: (cc.data_bits || 8).toString(), stop_bits: (cc.stop_bits || 1).toString(),
      description: cc.description || "",
      supported_tests: Array.isArray(record.supported_tests) ? record.supported_tests.join(", ") : "",
      hl7_version: pc.hl7_version || "2.3.1", ack_mode: pc.ack_mode || "original",
      mllp_enabled: pc.mllp_enabled !== undefined ? !!pc.mllp_enabled : true,
      message_types: Array.isArray(pc.message_types) ? pc.message_types.join(", ") : (pc.message_types || "ORU^R01"),
      qc_enabled: record.qc_enabled !== undefined ? !!record.qc_enabled : true,
      qc_frequency_hours: record.qc_frequency_hours?.toString() || "24",
      calibration_due_date: record.calibration_due_date ? record.calibration_due_date.slice(0, 10) : "",
      maintenance_due_date: record.maintenance_due_date ? record.maintenance_due_date.slice(0, 10) : "",
    });
  }, [record]);

  if (query?.isLoading) {
    return <div className="flex justify-center items-center min-h-[400px]"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }

  const handleSave = () => {
    const connection_config: Record<string, any> = { ...(record?.connection_config || {}) };
    if (form.description) connection_config.description = form.description;
    if (form.connection_type === "serial") {
      Object.assign(connection_config, {
        serial_port: form.serial_port, baud_rate: Number(form.baud_rate), parity: form.parity,
        data_bits: Number(form.data_bits), stop_bits: Number(form.stop_bits),
      });
    }
    const protocol_config: Record<string, any> = { ...(record?.protocol_config || {}) };
    if (form.protocol === "hl7") {
      Object.assign(protocol_config, {
        hl7_version: form.hl7_version, ack_mode: form.ack_mode, mllp_enabled: form.mllp_enabled,
        message_types: form.message_types.split(",").map((s: string) => s.trim()).filter(Boolean),
      });
    }
    mutate({ resource: "devices/analyzers", id: id as string, values: {
      name: form.name, category: form.category, manufacturer: form.manufacturer,
      model: form.model, serial_number: form.serial_number,
      connector_device: form.connector_device || undefined,
      connection_type: form.connection_type, protocol: form.protocol, status: form.status,
      device_ip: form.device_ip || undefined, device_port: form.device_port ? Number(form.device_port) : undefined,
      lis_ip: form.lis_ip || undefined, lis_port: form.lis_port ? Number(form.lis_port) : undefined,
      qc_enabled: form.qc_enabled, qc_frequency_hours: Number(form.qc_frequency_hours),
      calibration_due_date: form.calibration_due_date || undefined,
      maintenance_due_date: form.maintenance_due_date || undefined,
      connection_config, protocol_config,
      supported_tests: form.supported_tests ? form.supported_tests.split(",").map((s: string) => s.trim()).filter(Boolean) : [],
    }}, {
      onSuccess: () => { toast({ title: "Device updated successfully", variant: "success" }); navigate("/devices/analyzers"); },
      onError: (e: any) => toast({ title: e?.message || "Failed to save device", variant: "destructive" }),
    });
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="-ml-2"><ArrowLeft className="h-4 w-4" /></Button>
            <span>Edit Analyzer Device</span>
          </div>
        }
        actions={<Button onClick={handleSave} disabled={saving}><Save className="h-4 w-4 mr-2" />{saving ? "Saving..." : "Save Changes"}</Button>}
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle>Basic Information</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2"><Label>Device Name *</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} /></div>
            <div className="space-y-2"><Label>Manufacturer *</Label><Input value={form.manufacturer} onChange={(e) => set("manufacturer", e.target.value)} /></div>
            <div className="space-y-2"><Label>Model *</Label><Input value={form.model} onChange={(e) => set("model", e.target.value)} /></div>
            <div className="space-y-2"><Label>Serial Number *</Label><Input value={form.serial_number} onChange={(e) => set("serial_number", e.target.value)} /></div>
            <div className="space-y-2"><Label>Category *</Label>
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-2"><Label>Connector Device *</Label>
              <Select value={form.connector_device} onValueChange={(v) => set("connector_device", v)}>
                <SelectTrigger><SelectValue placeholder="Select connector device" /></SelectTrigger>
                <SelectContent>{(connectorOptions || []).map((o: any) => <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>)}</SelectContent>
              </Select></div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Connection Configuration</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2"><Label>Connection Type *</Label>
              <Select value={form.connection_type} onValueChange={(v) => set("connection_type", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{CONNECTION_TYPES.map((c) => <SelectItem key={c} value={c}>{c.toUpperCase()}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-2"><Label>Communication Protocol *</Label>
              <Select value={form.protocol} onValueChange={(v) => set("protocol", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{PROTOCOLS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-2"><Label>Device IP Address</Label><Input value={form.device_ip} onChange={(e) => set("device_ip", e.target.value)} placeholder="192.168.0.175" /></div>
            <div className="space-y-2"><Label>Device Port</Label><Input type="number" min={1} max={65535} value={form.device_port} onChange={(e) => set("device_port", e.target.value)} placeholder="6005" /></div>
          </CardContent>
        </Card>

        {form.connection_type === "serial" && (
        <Card>
          <CardHeader><CardTitle>Serial Port Configuration</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2"><Label>Serial Port</Label><Input value={form.serial_port} onChange={(e) => set("serial_port", e.target.value)} placeholder="COM11" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Baud Rate</Label>
                <Select value={form.baud_rate} onValueChange={(v) => set("baud_rate", v)}><SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{BAUD_RATES.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-2"><Label>Parity</Label>
                <Select value={form.parity} onValueChange={(v) => set("parity", v)}><SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{PARITIES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-2"><Label>Data Bits</Label>
                <Select value={form.data_bits} onValueChange={(v) => set("data_bits", v)}><SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{DATA_BITS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-2"><Label>Stop Bits</Label>
                <Select value={form.stop_bits} onValueChange={(v) => set("stop_bits", v)}><SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{STOP_BITS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
            </div>
          </CardContent>
        </Card>
        )}

        <Card>
          <CardHeader><CardTitle>LIS Integration Settings</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2"><Label>LIS IP Address</Label><Input value={form.lis_ip} onChange={(e) => set("lis_ip", e.target.value)} placeholder="192.168.0.171" /></div>
            <div className="space-y-2"><Label>LIS Port</Label><Input type="number" min={1} max={65535} value={form.lis_port} onChange={(e) => set("lis_port", e.target.value)} placeholder="5005" /></div>
          </CardContent>
        </Card>

        {form.protocol === "hl7" && (
        <Card>
          <CardHeader><CardTitle>HL7 Protocol Configuration</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>HL7 Version</Label>
                <Select value={form.hl7_version} onValueChange={(v) => set("hl7_version", v)}><SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{["2.2", "2.3", "2.3.1", "2.4", "2.5", "2.5.1"].map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-2"><Label>ACK Mode</Label>
                <Select value={form.ack_mode} onValueChange={(v) => set("ack_mode", v)}><SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="original">Original</SelectItem><SelectItem value="enhanced">Enhanced</SelectItem></SelectContent></Select></div>
            </div>
            <div className="flex items-center justify-between"><Label>MLLP Enabled</Label><Switch checked={!!form.mllp_enabled} onCheckedChange={(v) => set("mllp_enabled", v)} /></div>
            <div className="space-y-2"><Label>Message Types (comma separated)</Label><Input value={form.message_types} onChange={(e) => set("message_types", e.target.value)} placeholder="ORU^R01, ORM^O01" /></div>
          </CardContent>
        </Card>
        )}

        <Card>
          <CardHeader><CardTitle>Supported Tests</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2"><Label>Test Codes (comma separated)</Label><Textarea rows={3} value={form.supported_tests} onChange={(e) => set("supported_tests", e.target.value)} placeholder="WBC, RBC, HGB…" /></div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Quality Control & Status</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between"><Label>QC Enabled</Label><Switch checked={!!form.qc_enabled} onCheckedChange={(v) => set("qc_enabled", v)} /></div>
            <div className="space-y-2"><Label>QC Frequency (Hours)</Label><Input type="number" min={1} max={168} value={form.qc_frequency_hours} onChange={(e) => set("qc_frequency_hours", e.target.value)} /></div>
            <div className="space-y-2"><Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}><SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem><SelectItem value="maintenance">Maintenance</SelectItem></SelectContent></Select></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Calibration Due</Label><Input type="date" value={form.calibration_due_date} onChange={(e) => set("calibration_due_date", e.target.value)} /></div>
              <div className="space-y-2"><Label>Maintenance Due</Label><Input type="date" value={form.maintenance_due_date} onChange={(e) => set("maintenance_due_date", e.target.value)} /></div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AnalyzerDeviceEdit;
